import { faSignInAlt, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, Button, Link, Container } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import AppContent from '../../application/container/Container.tsx';
import { frontEndUrl, httpOnlySecurityService } from '../../application/iam';
import ipcCorporate from '../../assets/ipc_bg_corporate.svg';
import ipcNewBackground from '../../assets/app_backgroundpattern-1.svg';
import ipcnewBackground2 from '../../assets/app_backgroundpattern-2.svg';
import { useUser } from '../../hooks/useUser';
import { isPublicRoute } from '../../common/utils/routeUtils';

export function LoginPage() {
  const afterLoginUrl = `${frontEndUrl}/home`;
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Check if we're on a public route
  const isPublic = isPublicRoute(location.pathname);
  
  // Check token validity for redirect logic (not for fetching)
  const tokenValid = httpOnlySecurityService && !httpOnlySecurityService.isTokenExpired();
  const userAuthenticated = httpOnlySecurityService && httpOnlySecurityService.isUserAuthenticated();
  
  // Read user data from cache only - UserInformationProvider handles fetching
  // This prevents duplicate requests - we only read from React Query cache
  const { data: userInfo, isLoading: userLoading, isError: userError } = useUser(false);

  // Check authentication status when component mounts or when user data changes
  useEffect(() => {
    // On public routes, check token validity without making API calls
    if (isPublic) {
      if (tokenValid && userAuthenticated) {
        // If token appears valid, redirect to home (actual auth will be verified there)
        navigate('/home');
        return;
      }
      setIsCheckingAuth(false);
      return;
    }

    // For non-public routes, check if we have user data from TanStack Query cache
    if (userLoading) {
      // Still loading user data from cache
      setIsCheckingAuth(true);
      return;
    }

    if (userInfo && userInfo.username) {
      // User data is available from cache, user is authenticated
      setIsCheckingAuth(false);
      navigate('/home');
      return;
    }

    if (userError || !userInfo) {
      // No user data or error occurred, user is not authenticated
      setIsCheckingAuth(false);
      return;
    }
  }, [navigate, location.pathname, isPublic, tokenValid, userAuthenticated, userInfo, userLoading, userError]);

  const handleLogin = async () => {
    httpOnlySecurityService.login(afterLoginUrl);
  };

  // If still checking authentication, show minimal loading state
  if (isCheckingAuth) {
    return (
      <AppContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Typography variant="body1">Checking authentication status...</Typography>
        </Box>
      </AppContent>
    );
  }

  return (
    <AppContent>
      <Box
        sx={{
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
        data-testid="login-page-container"
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flexGrow: 1,
            width: '100%',
            maxWidth: '100vw',
            px: { xs: 2, sm: 3, md: 4 },
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
          data-testid="login-content-container"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: { xs: 2, sm: 3, md: 3.5 },
              py: { xs: 3, sm: 4, md: 5 },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'hidden',
              padding: '50px',
            }}
            data-testid="login-content-box"
          >
            <Typography
              variant='h2'
              sx={{
                color: '#CC0032',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 400,
                mt: { xs: 1, sm: 1.5 },
                width: '100%',
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              data-testid="login-subtitle"
            >
              International Postal Exchange Platform
            </Typography>

            <Typography
              variant='h1'
              sx={{
                color: '#003140',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 400,
                mt: { xs: 1, sm: 1.5 },
                lineHeight: 1.2,
                width: '100%',
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              data-testid="login-title"
            >
              Welcome to iPep
            </Typography>

            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                mt: { xs: 1, sm: 2 },
                overflowX: 'hidden',
              }}
              data-testid="login-description-container"
            >
              <Typography
                variant='body1'
                sx={{
                  color: '#003140',
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  lineHeight: { xs: 1.5, md: 1.75 },
                  overflowWrap: 'break-word',
                }}
                data-testid="login-description-1"
              >
                iPep streamlines the exchange of Interconnect and UPU accounting data. Automated
                validation ensures high quality, and users can easily upload, amend, and resubmit
                records.
              </Typography>

              <Typography
                variant='body1'
                sx={{
                  color: '#003140',
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  lineHeight: { xs: 1.5, md: 1.75 },
                  mt: { xs: 1.5, sm: 2 },
                  overflowWrap: 'break-word',
                }}
                data-testid="login-description-2"
              >
                The system provides structured reports for forecasting, automated notifications, and
                simplified document generation. It also manages Terminal Dues rates and centralizes
                accounting status for greater efficiency.
              </Typography>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                mt: { xs: 2, sm: 3 },
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
              }}
              data-testid="login-buttons-container"
            >
              <Button
                variant='contained'
                onClick={handleLogin}
                startIcon={<FontAwesomeIcon icon={faSignInAlt} />}
                sx={{
                  bgcolor: '#003140',
                  color: '#ffffff',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  '&:hover': {
                    bgcolor: '#004160',
                  },
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: '100%',
                }}
                data-testid="login-sso-button"
              >
                SSO LOGIN
              </Button>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                mt: { xs: 2, sm: 3 },
                color: '#003140',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                overflowWrap: 'break-word',
              }}
              data-testid="login-help-container"
            >
              <Typography
                variant='body1'
                sx={{
                  display: 'inline',
                  fontSize: 'inherit',
                  overflowWrap: 'break-word',
                }}
                data-testid="login-help-text"
              >
                Having trouble logging in? Reach out to IPC Service Desk by email{' '}
              </Typography>
              <Link
                href='mailto:servicedesk@ipc.be'
                sx={{
                  color: '#003140',
                  textDecorationColor: '#003140',
                  '&:hover': {
                    color: '#004160',
                  },
                  fontSize: 'inherit',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
                data-testid="login-help-email"
              >
                servicedesk@ipc.be
              </Link>
              <Typography
                variant='body1'
                sx={{
                  display: 'inline',
                  fontSize: 'inherit',
                  overflowWrap: 'break-word',
                }}
              >
                {' '}
                or{' '}
              </Typography>
              <Link
                href='https://servicedesk.ipc.be/servicedesk'
                sx={{
                  color: '#003140',
                  textDecorationColor: '#003140',
                  '&:hover': {
                    color: '#004160',
                  },
                  fontSize: 'inherit',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
                data-testid="login-help-portal"
              >
                IPC Customer Service Desk Portal
              </Link>
            </Box>
          </Box>
        </Container>
        <Box
          component='img'
          src={ipcnewBackground2}
          alt='IPC logo'
          sx={{
            width: '100%',
            maxWidth: '100vw',
            height: 'auto',
            mt: { xs: 2, sm: 3 },
            display: 'block',
            boxSizing: 'border-box',
            transform: 'scaleY(-1)',
          }}
          data-testid="login-corporate-image"
        />
      </Box>
    </AppContent>
  );
}
